import { Button, Heading, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Asset, type AssetType, pluralize, type ProcedureType, type Stored } from "@overmap-ai/common"
import {
	selectAssetsOfProject,
	selectAssetTypeById,
	selectProcedureTypeById,
	selectProcedureTypesOfOrganization,
} from "@overmap-ai/core"
import { useSyncedState } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FormEventHandler, memo, useCallback, useMemo, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { AssetMultiSelect } from "../../components/DropdownMultiSelects/AssetMultiSelectDropdown"
import { AssetTypeSelect } from "../../components/DropdownSelects/AssetTypeSelect"
import { ProcedureTypeDropdownSelect } from "../../components/DropdownSelects/ProcedureTypeDropdownSelect"
import { AssetTypeIcon } from "../../components/Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../../components/Icons/ProcedureTypeIcon"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectAssetTypesOfProject } from "../../store/selectors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/initiatives/create")

export const CreateProcedureInitiativeView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()
	const { asset_type: assetTypeId, procedure_type: procedureTypeId, redirect } = routeApi.useSearch()

	const defaultAssetType = useAppSelector(selectAssetTypeById(assetTypeId ?? ""))

	if (!!assetTypeId && !defaultAssetType) {
		throw new Error(`Cant find AssetType with uuid ${assetTypeId} in store.`)
	}

	const defaultProcedureType = useAppSelector(selectProcedureTypeById(procedureTypeId ?? ""))

	if (!!procedureTypeId && !defaultProcedureType) {
		throw new Error(`Cant find ProcedureType with uuid ${procedureTypeId} in store.`)
	}

	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfProject(projectId))
	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(organizationId))
	const assets = useShallowEqaulSelector(selectAssetsOfProject(projectId))
	const navigate = useNavigate()

	const [selectedAssetType, setSelectedAssetType] = useSyncedState<Stored<AssetType> | undefined>(defaultAssetType)
	const [selectedProcedureType, setSelectedProcedureType] = useSyncedState<Stored<ProcedureType> | undefined>(
		defaultProcedureType,
	)
	const [selectedAssets, setSelectedAssets] = useState<Stored<Asset>[]>([])

	const handleSelectedAssetTypeChange = useCallback(
		(assetType?: Stored<AssetType>) => {
			setSelectedAssetType(assetType)
			setSelectedAssets([])
		},
		[setSelectedAssetType],
	)

	const assetsOfAssetType = useMemo(() => {
		return assets.filter(({ asset_type }) => asset_type === selectedAssetType?.uuid)
	}, [assets, selectedAssetType?.uuid])

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(event) => {
			event.preventDefault()
			event.stopPropagation()

			if (!selectedAssetType || !selectedProcedureType || selectedAssets.length === 0) return

			const [offlineProcedureInitiative] = sdk.procedureInitiatives.add({
				project: projectId,
				procedure_type: selectedProcedureType.uuid,
				asset_type: selectedAssetType.uuid,
			})

			void sdk.procedures.bulkAdd(
				selectedAssets.map((asset) => ({
					project: projectId,
					asset: asset.uuid,
					issue: null,
					procedure_type: selectedProcedureType.uuid,
					procedure_initiative: offlineProcedureInitiative.uuid,
				})),
			)

			if (redirect) {
				void navigate({
					href: redirect,
					replace: true,
				})
			} else {
				void navigate({
					from: "/$organizationId/$projectId/initiatives/create",
					to: "/$organizationId/$projectId/initiatives",
					replace: true,
				})
			}
		},
		[selectedAssetType, selectedProcedureType, selectedAssets, projectId, navigate, redirect],
	)

	const disabled = !selectedAssetType || !selectedProcedureType || selectedAssets.length === 0

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<Heading size="xl">Create initiative</Heading>

				<Separator size="full" />

				<form className="flex w-full flex-col gap-4" onSubmit={handleSubmit}>
					<div className="flex flex-col gap-1">
						<Text accentColor="base" size="sm">
							Asset type
						</Text>
						<AssetTypeSelect
							size="sm"
							required={false}
							assetTypes={assetTypes}
							value={selectedAssetType}
							onValueChange={handleSelectedAssetTypeChange}
						>
							<Button variant="surface" accentColor="base" size="sm" className="justify-between!">
								{selectedAssetType ? (
									<div className="flex items-center gap-2">
										<AssetTypeIcon assetTypeId={selectedAssetType.uuid} />
										<Text>{selectedAssetType.name}</Text>
									</div>
								) : (
									<>Select asset type</>
								)}
								<LuIcon icon={modelIconMapping.chevronDown} />
							</Button>
						</AssetTypeSelect>
					</div>

					<div className="flex flex-col gap-1">
						<Text accentColor="base" size="sm">
							Procedure type
						</Text>
						<ProcedureTypeDropdownSelect
							size="sm"
							required={false}
							procedureTypes={procedureTypes}
							value={selectedProcedureType}
							onValueChange={setSelectedProcedureType}
						>
							<Button variant="surface" accentColor="base" size="sm" className="justify-between!">
								{selectedProcedureType ? (
									<div className="flex items-center gap-2">
										<ProcedureTypeIcon procedureTypeId={selectedProcedureType.uuid} />
										<Text>{selectedProcedureType.name}</Text>
									</div>
								) : (
									<>Select procedure type</>
								)}
								<LuIcon icon={modelIconMapping.chevronDown} />
							</Button>
						</ProcedureTypeDropdownSelect>
					</div>

					{selectedAssetType ? (
						<div className="flex flex-col gap-1 overflow-hidden">
							<Text accentColor="base" size="sm">
								Assets
							</Text>

							<AssetMultiSelect
								size="sm"
								assets={assetsOfAssetType}
								values={selectedAssets}
								onValuesChange={setSelectedAssets}
							>
								<Button variant="surface" accentColor="base" size="sm" className="justify-between!">
									{selectedAssets.length} {pluralize("selected asset", selectedAssets.length)}
									<LuIcon icon={modelIconMapping.chevronDown} />
								</Button>
							</AssetMultiSelect>
						</div>
					) : null}

					<Button className="w-max" type="submit" variant="surface" size="sm" disabled={disabled}>
						Create
					</Button>
				</form>
			</section>
		</ViewContainer>
	)
})
CreateProcedureInitiativeView.displayName = "CreateProcedureInitiativeView"
